import React, {
  useCallback, useMemo, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import Logo from '../../../components/logo/logo.component';
import Divider from '../../../components/divider/divider.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import NavigationLogo from '../../../components/navigation-logo/navigation-logo.component';
import NavigationContainer from '../../../components/navigation-container/navigation-container.component';
import NavigationDrawer from '../../../components/navigation-drawer/navigation-drawer.component';
import NavigationContent from '../../../components/navigation-content/navigation-content.component';
import NavigationContentItem from '../../../components/navigation-content-item/navigation-content-item.component';
import List from '../../../components/list/list.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import DashboardIcon from '../../../icons/dashboard-icon';
import AssetPortfolioIcon from '../../../icons/asset-portfolio-icon';
import CapitalRaiseIcon from '../../../icons/capital-raise-icon';
import DealPipelineIcon from '../../../icons/deal-pipeline-icon';
import RelationshipCoverageIcon from '../../../icons/relationship-coverage-icon';
import ReportsIcon from '../../../icons/reports-icon';
import SearchIcon from '../../../icons/search-icon';
import HelpIcon from '../../../icons/help-icon';
import ProfileIcon from '../../../icons/profile-icon';
import SettingsIcon from '../../../icons/settings-icon';
import SignOutIcon from '../../../icons/sign-out-icon';
import MainService from '../../../services/main-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import { toggleRightDrawer } from '../right-drawer/right-drawer.action';
import toggleNavigationDrawer from './navigation.action';
import {
  ACTIVITY_LIST_VIEW, ASSET_LIST_VIEW, CONTENT_PAGE_VIEW, DASHBOARD_VIEW,
  DEAL_LIST_VIEW, SEARCH_RESULT_VIEW, SETTINGS_PROFILE_VIEW, REPORT_LIST_VIEW, CONTACT_VIEW,
} from '../../../constants/entity/permissions';
import {
  ROUTE_ACTIVITY_LIST, ROUTE_ASSET_LIST, ROUTE_CONTACT_LIST, ROUTE_DASHBOARD, ROUTE_DEAL_LIST,
  ROUTE_REPORT_LIST, ROUTE_SEARCH, ROUTE_SETTINGS, ROUTE_SETTINGS_PROFILE, ROUTE_LOGOUT,
} from '../../../constants/system/routes';
import { QUERY_PARAM_CATEGORY } from '../../../constants/system/query-params';
import { ACTIVITY_CATEGORY_CAPITAL_RAISE } from '../../../constants/entity/activity-categories';

const Navigation: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const HELP_PAGE_ROUTE_MAIN: string = '/';
  const SETTING_NAME_SHOW_ACCOUNT_LOGO: string = 'show-account-logo';

  const {
    isRightDrawerOpen,
    isNavigationDrawerOpen,
    currentUser,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const dispatch: ReduxDispatch = useDispatch();

  const accountData: Account | null = (currentUser && currentUser.account) || null;
  const accountSettings: Setting[] = (accountData && accountData.settings) || [];
  const showAccountLogoSetting: Setting | null = accountSettings && accountSettings.length
    ? (accountSettings.find((settingItem: Setting): boolean => settingItem.name === SETTING_NAME_SHOW_ACCOUNT_LOGO)
      || null) : null;
  const isAccountLogoVisible: boolean = showAccountLogoSetting ? showAccountLogoSetting.value : false;
  const isNavigationInvisible: boolean = isSmallScreen || (!isSmallScreen && !isNavigationDrawerOpen);
  const helpHostName: string | undefined = MainService.HELP_HOST;

  const onToggleNavigationDrawer: MouseEventHandlerFunction = useCallback((): void => {
    if (isSmallScreen) {
      dispatch(toggleNavigationDrawer());
    }
  }, [isSmallScreen]);

  const onToggleRightDrawer: MouseEventHandlerFunction = useCallback((): void => {
    if (isSmallScreen) {
      dispatch(toggleRightDrawer());
    }
  }, [isSmallScreen]);

  const onMenuItemClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    onToggleNavigationDrawer(event);
  }, [isSmallScreen]);

  const onLogoClick: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    if (isNavigationDrawerOpen) {
      onToggleNavigationDrawer(event);
    }

    if (isRightDrawerOpen) {
      onToggleRightDrawer(event);
    }
  }, [isSmallScreen, isNavigationDrawerOpen, isRightDrawerOpen]);

  const menuItems: NavigationMenuItem[] = useMemo((): NavigationMenuItem[] => [
    {
      id: 1,
      icon: <DashboardIcon />,
      name: 'Dashboard',
      link: ROUTE_DASHBOARD,
      route: ROUTE_DASHBOARD,
      permission: DASHBOARD_VIEW,
    },
    {
      id: 2,
      icon: <AssetPortfolioIcon />,
      name: 'Asset Portfolio',
      link: ROUTE_ASSET_LIST,
      route: ROUTE_ASSET_LIST,
      permission: ASSET_LIST_VIEW,
    },
    {
      id: 3,
      icon: <DealPipelineIcon />,
      name: 'Deal Pipeline',
      link: ROUTE_DEAL_LIST,
      route: ROUTE_DEAL_LIST,
      permission: DEAL_LIST_VIEW,
    },
    {
      id: 4,
      icon: <CapitalRaiseIcon />,
      name: 'Capital Raise',
      link: `${ROUTE_ACTIVITY_LIST}?${QUERY_PARAM_CATEGORY}=${ACTIVITY_CATEGORY_CAPITAL_RAISE}`,
      route: ROUTE_ACTIVITY_LIST,
      permission: ACTIVITY_LIST_VIEW,
    },
    {
      id: 5,
      icon: <RelationshipCoverageIcon />,
      name: 'Relationship Coverage',
      link: ROUTE_CONTACT_LIST,
      route: ROUTE_CONTACT_LIST,
      permission: CONTACT_VIEW,
    },
    {
      id: 6,
      icon: <ReportsIcon />,
      name: 'Reports',
      link: ROUTE_REPORT_LIST,
      route: ROUTE_REPORT_LIST,
      permission: REPORT_LIST_VIEW,
    },
  ], []);

  const mobileMenuItems: NavigationMenuItem[] = useMemo((): NavigationMenuItem[] => [
    {
      id: 1,
      icon: <SearchIcon />,
      name: 'Search',
      link: ROUTE_SEARCH,
      route: ROUTE_SEARCH,
      permission: SEARCH_RESULT_VIEW,
    },
    {
      id: 2,
      icon: <ProfileIcon />,
      name: 'Profile',
      link: ROUTE_SETTINGS_PROFILE,
      permission: SETTINGS_PROFILE_VIEW,
    },
    {
      id: 3,
      icon: <SettingsIcon />,
      name: 'Settings',
      link: ROUTE_SETTINGS,
      permission: SETTINGS_PROFILE_VIEW,
    },
    {
      id: 4,
      icon: <HelpIcon />,
      name: 'Help',
      link: helpHostName ? `${helpHostName}${HELP_PAGE_ROUTE_MAIN}` : undefined,
      permission: CONTENT_PAGE_VIEW,
      isExternal: true,
    },
    {
      id: 5,
      icon: <SignOutIcon />,
      name: 'Sign Out',
      link: ROUTE_LOGOUT,
    },
  ], []);

  const navigationPermissions: string[] = (isSmallScreen ? [...menuItems, ...mobileMenuItems] : menuItems)
    && menuItems.map((menuItem: NavigationMenuItem): string | undefined => menuItem.permission)
      .filter((permission: string | undefined): boolean => !!permission) as string[];

  return (
    <CheckAccess permissions={navigationPermissions}>
      {
        !!currentUser && (
          <NavigationContainer isInvisible={isNavigationInvisible}>
            <NavigationDrawer
              isSwipeable={isSmallScreen}
              isOpen={isNavigationDrawerOpen}
              onOpen={onToggleNavigationDrawer}
              onClose={onToggleNavigationDrawer}
            >
              {
                isSmallScreen && (
                  <FlexContainer>
                    <Logo linkRoute={ROUTE_DASHBOARD} linkPermission={DASHBOARD_VIEW} onClick={onLogoClick} />
                  </FlexContainer>
                )
              }
              <NavigationContent onClick={onToggleNavigationDrawer}>
                <List withOffset>
                  {
                    isAccountLogoVisible && accountData && (accountData.photo || accountData.photoThumbnail) && (
                      <NavigationLogo
                        title={accountData.name}
                        imageLink={accountData.photo || accountData.photoThumbnail}
                        onClick={onMenuItemClick}
                      />
                    )
                  }
                  {
                    menuItems.map((menuItem: NavigationMenuItem): ReactElement => (
                      <NavigationContentItem key={menuItem.id} menuItem={menuItem} onClick={onMenuItemClick} />
                    ))
                  }
                  {
                    isSmallScreen && (
                      <Fragment>
                        <Divider isLight withOffset />
                        {
                          mobileMenuItems.map((menuItem: NavigationMenuItem): ReactElement => (
                            <NavigationContentItem key={menuItem.id} menuItem={menuItem} onClick={onMenuItemClick} />
                          ))
                        }
                      </Fragment>
                    )
                  }
                </List>
              </NavigationContent>
            </NavigationDrawer>
          </NavigationContainer>
        )
      }
    </CheckAccess>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    navigation,
    rightDrawer,
  } = reducers;

  return {
    currentUser: app.baseData && app.baseData.currentUser,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    isNavigationDrawerOpen: navigation.isDrawerOpen,
  };
};

export default connect(mapState)(Navigation);
